/**
 * What a click does to the selection, as a pure function, shared by the
 * Outliner and the viewport. Modifiers follow Unreal: a plain click replaces,
 * Ctrl (Cmd on macOS) toggles, and Shift adds in the viewport or extends a
 * range in the Outliner, where rows have an order. The range grows from a
 * fixed anchor (the last plain or Ctrl click), like Windows Explorer.
 */
import type { EntityId } from "../../world/entity";
import type { SelectionState } from "./Selection";

export type ClickMode = "replace" | "toggle" | "add" | "range";

export type ClickIntent =
  | { readonly mode: "replace" | "toggle" | "add" }
  | {
      readonly mode: "range";
      /** Where the range starts; null falls back to a plain click. */
      readonly anchor: EntityId | null;
      /** The rows in display order. */
      readonly order: readonly EntityId[];
    };

export interface ModifierKeys {
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
}

/** Ctrl/Cmd toggles; Shift does what the surface's `shift` means (add, or a range). */
export function clickMode<Shift extends "add" | "range">(
  keys: ModifierKeys,
  shift: Shift,
): "replace" | "toggle" | Shift {
  if (keys.ctrlKey || keys.metaKey) return "toggle";
  return keys.shiftKey ? shift : "replace";
}

export function selectionAfterClick(
  current: SelectionState,
  clicked: EntityId,
  intent: ClickIntent,
): SelectionState {
  switch (intent.mode) {
    case "replace":
      return { ids: [clicked], active: clicked };
    case "toggle":
      return current.ids.includes(clicked)
        ? remove(current, clicked)
        : { ids: [...current.ids, clicked], active: clicked };
    case "add":
      // Re-adding moves it to the end: it's the newest, and the active one.
      return { ids: [...current.ids.filter((id) => id !== clicked), clicked], active: clicked };
    case "range":
      return range(clicked, intent.anchor, intent.order);
  }
}

/** Removes an entity; the active one becomes the most recently selected of the rest. */
function remove(current: SelectionState, id: EntityId): SelectionState {
  const ids = current.ids.filter((selected) => selected !== id);
  const active = current.active === id ? (ids.at(-1) ?? null) : current.active;
  return { ids, active };
}

/** The rows from the anchor to the click, in display order; the click is active. */
function range(
  clicked: EntityId,
  anchor: EntityId | null,
  order: readonly EntityId[],
): SelectionState {
  const from = anchor === null ? -1 : order.indexOf(anchor);
  const to = order.indexOf(clicked);
  if (from === -1 || to === -1) return { ids: [clicked], active: clicked };

  const [start, end] = from <= to ? [from, to] : [to, from];
  return { ids: order.slice(start, end + 1), active: clicked };
}
